/**
 * Pure calculation functions extracted from bond-calc without React/Three.js dependencies
 * This module provides bond geometry calculations for SVG rendering
 */

import { v3 } from "@alchemiesolns/maths";
import { VisibilityFlag } from "@alchemiesolns/visibility-config";

// Helper functions
const isCToC = (a1: string, a2: string) => {
    return a1 === "C" && a2 === "C";
}

const isCToNC = (a1: string, a2: string) => {
    return ((a1 === "C" && a2 !== "C") || (a1 !== "C" && a2 === "C"));
}

// Type definitions
export interface AttributeProps {
    bondSize: number;
    a1: string;  // element symbol
    a2: string;  // element symbol
    isInRing: boolean;
    a1Count: number;
    a2Count: number;
    format: FormatOptions;
    a1VisibilityFlag?: VisibilityFlag;
    a2VisibilityFlag?: VisibilityFlag;
    offsetDirection?: number;  // +1 or -1, determines which side of bond the offset goes
}

export interface tubeAttributeProps {
    oneVisible: boolean;
    twoVisible: boolean;
    threeVisible: boolean;
    oneOffset: number;
    twoOffset: number;
    threeOffset: number;
    oneLength: number;
    twoLength: number;
    threeLength: number;
    centered?: boolean;
}

// Minimal format options interface (accepting any format object)
// This allows compatibility with both minimal configs and full FormatOptions
export interface FormatOptions {
    bond: {
        offset: number;
        length: number;
        ratios: {
            single: {
                carbonToCarbon: number;
                carbonToNonCarbon: number;
                nonCarbonToNonCarbon: number;
            };
            double: {
                carbonToCarbon: {
                    centered: number;
                    offset: number;
                };
                carbonToNonCarbon: {
                    centered: number;
                    offset: number;
                };
                carbonToNonCarbonRing: number;
                nonCarbonToNonCarbon: number;
            };
            triple: {
                carbonToCarbon: number;
                carbonToNonCarbon: number;
                nonCarbonToNonCarbon: number;
            };
        };
    };
    [key: string]: any; // Allow additional properties for full compatibility
}

export const calculateLineAngleTubeAttributes = ({
    bondSize,
    a1,
    a2,
    isInRing,
    a1Count,
    a2Count,
    format,
    a1VisibilityFlag,
    a2VisibilityFlag,
    offsetDirection = 1
}: AttributeProps): tubeAttributeProps => {
    // Treat carbons with ForceText or ForceTextHiddenLP flag as non-carbons for bond calculations
    // This ensures bonds terminate properly at text labels rather than extending through
    const effectiveA1 = (a1 === 'C' && (a1VisibilityFlag === VisibilityFlag.ForceText || a1VisibilityFlag === VisibilityFlag.ForceTextHiddenLP)) ? 'X' : a1;
    const effectiveA2 = (a2 === 'C' && (a2VisibilityFlag === VisibilityFlag.ForceText || a2VisibilityFlag === VisibilityFlag.ForceTextHiddenLP)) ? 'X' : a2;

    // Single bond
    if (bondSize === 1) {
        if (isCToC(effectiveA1, effectiveA2)) {
            return {
                oneVisible: true,
                twoVisible: false,
                threeVisible: false,
                oneOffset: 0,
                twoOffset: 0,
                threeOffset: 0,
                oneLength: format.bond.ratios.single.carbonToCarbon,
                twoLength: format.bond.ratios.single.carbonToCarbon,
                threeLength: format.bond.ratios.single.carbonToCarbon,
            };
        } else if (isCToNC(effectiveA1, effectiveA2)) {
            return {
                oneVisible: true,
                twoVisible: false,
                threeVisible: false,
                oneOffset: 0,
                twoOffset: 0,
                threeOffset: 0,
                oneLength: format.bond.ratios.single.carbonToNonCarbon,
                twoLength: format.bond.ratios.single.carbonToNonCarbon,
                threeLength: format.bond.ratios.single.carbonToNonCarbon,
            };
        } else {
            return {
                oneVisible: true,
                twoVisible: false,
                threeVisible: false,
                oneOffset: 0,
                twoOffset: 0,
                threeOffset: 0,
                oneLength: format.bond.ratios.single.nonCarbonToNonCarbon,
                twoLength: format.bond.ratios.single.nonCarbonToNonCarbon,
                threeLength: format.bond.ratios.single.nonCarbonToNonCarbon,
            };
        }
    }

    // Double bond
    if (bondSize === 2) {
        const centered = {
            oneVisible: true,
            twoVisible: true,
            threeVisible: false,
            oneOffset: format.bond.offset / 2,
            twoOffset: -format.bond.offset / 2,
            threeOffset: 0,
            oneLength: format.bond.ratios.double.carbonToCarbon.centered,
            twoLength: format.bond.ratios.double.carbonToCarbon.centered,
            threeLength: 1,
            centered: true
        };
        const offset = {
            oneVisible: true,
            twoVisible: true,
            threeVisible: false,
            oneOffset: 0,
            twoOffset: format.bond.offset * offsetDirection,
            threeOffset: 0,
            oneLength: 1,
            twoLength: format.bond.ratios.double.carbonToCarbon.offset,
            threeLength: 1,
        };
        if (isCToC(effectiveA1, effectiveA2)) {
            // No-substitution
            if (a1Count + a2Count === 0) {
                return centered;
            // Monosubstituted
            } else if (a1Count + a2Count === 1) {
                return offset;
            // Disubstituted
            } else if (a1Count + a2Count === 2) {
                // both on one carbon
                if (a2Count === 0 || a2Count === 2) {
                    return centered;
                } else {
                    return offset;
                }
            // Trisubstituted
            } else {
                return offset;
            }
        } else if (isCToNC(effectiveA1, effectiveA2)) {

            const centered = {
                oneVisible: true,
                twoVisible: true,
                threeVisible: false,
                oneOffset: format.bond.offset / 2,
                twoOffset: -format.bond.offset / 2,
                threeOffset: 0,
                oneLength: format.bond.ratios.double.carbonToNonCarbon.centered,
                twoLength: format.bond.ratios.double.carbonToNonCarbon.centered,
                threeLength: 1,
                centered: true
            };
            const offset = {
                oneVisible: true,
                twoVisible: true,
                threeVisible: false,
                oneOffset: format.bond.offset * offsetDirection,
                twoOffset: 0,
                threeOffset: 0,
                oneLength: format.bond.ratios.double.carbonToNonCarbonRing,
                twoLength: format.bond.ratios.double.carbonToNonCarbon.offset,
                threeLength: 1,
            };

            const carbonSub = effectiveA1 === "C" ? a1Count : a2Count;
            // C=O in a ring
            if (isInRing) {
                return offset;
            // C=N NOT in a ring and C is disubstituted
            } else if (!isInRing && carbonSub === 2) {
                return centered;
            // C=N NOT in a ring and C is monosubstituted
            } else if (!isInRing && carbonSub === 1) {
                return offset;  
            // Not in ring
            } else {
                return offset;
            }
        } else {
            return {
                oneVisible: true,
                twoVisible: true,
                threeVisible: false,
                oneOffset: format.bond.offset / 2,
                twoOffset: format.bond.offset * -1 / 2,
                threeOffset: 0,
                oneLength: format.bond.ratios.double.nonCarbonToNonCarbon,
                twoLength: format.bond.ratios.double.nonCarbonToNonCarbon,
                threeLength: 1,
            };
        }
    }   

    // Triple bond
    if (bondSize === 3) {
        if (isCToC(effectiveA1, effectiveA2)) {
            return {
                oneVisible: true,
                twoVisible: true,
                threeVisible: true,
                oneOffset: 0,
                twoOffset: format.bond.offset * -1,
                threeOffset: format.bond.offset,
                oneLength: format.bond.ratios.triple.carbonToCarbon,
                twoLength: format.bond.ratios.triple.carbonToCarbon,
                threeLength: format.bond.ratios.triple.carbonToCarbon,
            };
        } else if (isCToNC(effectiveA1, effectiveA2)) {
            return {
                oneVisible: true,
                twoVisible: true,
                threeVisible: true,
                oneOffset: 0,
                twoOffset: format.bond.offset * -1,
                threeOffset: format.bond.offset,
                oneLength: format.bond.ratios.triple.carbonToNonCarbon,
                twoLength: format.bond.ratios.triple.carbonToNonCarbon,
                threeLength: format.bond.ratios.triple.carbonToNonCarbon,
            };
        } else {
            return {
                oneVisible: true,
                twoVisible: true,
                threeVisible: true,
                oneOffset: 0,
                twoOffset: format.bond.offset * -1,
                threeOffset: format.bond.offset,
                oneLength: format.bond.ratios.triple.nonCarbonToNonCarbon,
                twoLength: format.bond.ratios.triple.nonCarbonToNonCarbon,
                threeLength: format.bond.ratios.triple.nonCarbonToNonCarbon,
            };
        }
    }
    console.log("INVALID")
    // Invalid size
    return {
        oneVisible: false,
        twoVisible: false,
        threeVisible: false,
        oneOffset: 0,
        twoOffset: 0,
        threeOffset: 0,
        oneLength: 1,
        twoLength: 1,
        threeLength: 1,
    };
}

export const computeLineAngleBondEndPoints = (
    atomicNum1: string,
    atomicNum2: string,
    a1Pos: number[],
    a2Pos: number[],
    a1Count: number,
    a2Count: number,
    o: number,
    bondRatio: number,
    id: number,
    centered = false,
    format: FormatOptions,
    a1VisibilityFlag?: VisibilityFlag,
    a2VisibilityFlag?: VisibilityFlag
) => {
    // Treat carbons with ForceText or ForceTextHiddenLP flag as non-carbons for bond endpoint calculations
    // This ensures bonds terminate properly at text labels rather than extending through
    const effectiveAtom1 = (atomicNum1 === 'C' && (a1VisibilityFlag === VisibilityFlag.ForceText || a1VisibilityFlag === VisibilityFlag.ForceTextHiddenLP)) ? 'X' : atomicNum1;
    const effectiveAtom2 = (atomicNum2 === 'C' && (a2VisibilityFlag === VisibilityFlag.ForceText || a2VisibilityFlag === VisibilityFlag.ForceTextHiddenLP)) ? 'X' : atomicNum2;

    const endPoints = {a1: a1Pos, a2: a2Pos};
    const dir = v3.directionTo(a1Pos, a2Pos);
    // Determine endpoints offset (vertical offset)
    //N=N
    if (effectiveAtom1 !== "C" && effectiveAtom2 !== "C") {
        const offset = v3.multiplyScalar(dir, format.bond.length * (1-bondRatio) / 2);
        endPoints.a1 = v3.addVectors(endPoints.a1, offset);
        endPoints.a2 = v3.subVectors(endPoints.a2, offset);
    // C=C
    } else if (effectiveAtom1 === "C" && effectiveAtom2 === "C") {
        // Disubstituted - both substituents on same carbon
        if (o === 2 && ((a1Count === 2 || a2Count === 2) && a1Count + a2Count === 2)) {
            const offset = v3.multiplyScalar(dir, format.bond.length * (1-bondRatio));
            if (a2Count === 2)
                endPoints.a2 = v3.subVectors(endPoints.a2, offset);
            else 
                endPoints.a1= v3.addVectors(endPoints.a1, offset);
        } else {
            const offset = v3.multiplyScalar(dir, format.bond.length * (1-bondRatio) / 2);
            // const isChainEnd = o === 2 ? isChainEndVisibleH(b.a1, b.a2) : undefined;
            const isChainEnd = undefined;

            if (isChainEnd !== "a1") {
                endPoints.a1 = v3.addVectors(endPoints.a1, offset);
            }
            if (isChainEnd !== "a2") {
                endPoints.a2 = v3.subVectors(endPoints.a2, offset);
            }
        }
    // C=N
    } else {
        // const isCentered = format.bond.ratios.double.carbonToNonCarbon.centered === bondRatio ? true : false;
        if (o === 2 && id === 1 && !centered) {
            const carbonOffset = v3.multiplyScalar(dir, format.bond.length * (1-(bondRatio))/2);
            const nonCarbonOffset = v3.multiplyScalar(dir, format.bond.length * (1-(format.bond.ratios.double.carbonToNonCarbon.offset)));
            if (effectiveAtom1 !== "C") {
                endPoints.a1 = v3.addVectors(endPoints.a1, nonCarbonOffset);
                endPoints.a2 = v3.subVectors(endPoints.a2, carbonOffset);
            }
            if (effectiveAtom2 !== "C") {
                endPoints.a1 = v3.addVectors(endPoints.a1, carbonOffset);
                endPoints.a2 = v3.subVectors(endPoints.a2, nonCarbonOffset);
            }
        } else {
            const offset = v3.multiplyScalar(dir, format.bond.length * (1-(bondRatio)));
            const carbonOffset = v3.multiplyScalar(dir, format.bond.length * .05);
            if (effectiveAtom1 !== "C") {
                endPoints.a1 = v3.addVectors(endPoints.a1, offset);
                if (centered) endPoints.a2 = v3.addVectors(endPoints.a2, carbonOffset);
            }
            if (effectiveAtom2 !== "C") {
                endPoints.a2 = v3.subVectors(endPoints.a2, offset);
                if (centered) endPoints.a1 = v3.subVectors(endPoints.a1, carbonOffset);
            }
        }
    }
    return endPoints;
}

// Pure version of BondTubeUtil.computeBondEndPoints without Three.js
export const computeBondEndPoints = (
    pos1: number[], 
    pos2: number[], 
    camDir: number[], 
    offset: number
): { a1: number[], a2: number[] } => {
    const dir = v3.normalize(v3.subVectors(pos1, pos2));
    const perp = v3.normalize(v3.crossVectors(camDir, dir));
    const off = v3.multiplyScalar(perp, offset);
    const final1 = v3.addVectors(pos1, off);
    const final2 = v3.addVectors(pos2, off);
    
    return {
        a1: final1,
        a2: final2
    };
}